import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { Checkbox, FormController, StrongText, StyledFlex, StyledMargin } from '@dreryk/react-components';
import { Translate, useFormController } from '@dreryk/react-common';

import { patientPhoneNotificationTranslationKeys } from '../../../../translations';

import {
   PatientPhoneNotificationVideoConsultationReminderConfigurationFormBean,
   PatientPhoneNotificationVideoConsultationReminderConfigurationFormController,
   PatientPhoneNotificationVideoConsultationReminderConfigurationFormInitializer,
} from './PatientPhoneNotificationVideoConsultationReminderConfigurationForm.types';

const t = patientPhoneNotificationTranslationKeys.videoConsultationConfiguration.form;

type PatientPhoneNotificationVideoConsultationReminderConfigurationFormRenderProps = Pick<
   PatientPhoneNotificationVideoConsultationReminderConfigurationFormController,
   'handleSubmit'
>;

type PatientPhoneNotificationVideoConsultationReminderConfigurationFormProps = {
   initializer: PatientPhoneNotificationVideoConsultationReminderConfigurationFormInitializer;
   children: (
      renderProps: PatientPhoneNotificationVideoConsultationReminderConfigurationFormRenderProps
   ) => JSX.Element;
};

// eslint-disable-next-line max-lines-per-function
export const PatientPhoneNotificationVideoConsultationReminderConfigurationForm = (
   props: PatientPhoneNotificationVideoConsultationReminderConfigurationFormProps
): JSX.Element => {
   const {
      setError,
      setValue,
      watch,
      register,
      handleSubmit,
   } = useFormController<PatientPhoneNotificationVideoConsultationReminderConfigurationFormBean>(
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useMemo(() => props.initializer(), [props.initializer])
   );

   const { earlyNotificationEnabled, lateNotificationEnabled } = watch([
      'earlyNotificationEnabled',
      'lateNotificationEnabled',
   ]);

   useUpdateEffect(() => {
      if (!earlyNotificationEnabled) {
         setValue('cancellationAfterEarlyNotificationEnabled', false);
      }
      if (!lateNotificationEnabled) {
         setValue('cancellationAfterLateNotificationEnabled', false);
      }

      if (earlyNotificationEnabled || lateNotificationEnabled) {
         setError('videoConsultationReminderConfigurationValidation', false);
         setError('lateNotificationEnabled', false);
         setError('earlyNotificationEnabled', false);
      }
   }, [earlyNotificationEnabled, lateNotificationEnabled]);

   return (
      <StyledFlex as={'form'} direction={'column'} gap={'4px'} noValidate>
         <StyledFlex direction={'column'} gap={'14px'}>
            <StrongText testId={'label'}>
               <Translate>{t.label}</Translate>
            </StrongText>
            <StyledFlex direction={'column'} gap={'10px'}>
               <Checkbox
                  label={t.lateNotificationEnabled}
                  controller={register('lateNotificationEnabled')}
                  testId={'lateNotificationEnabled'}
               />
               <StyledMargin left={26}>
                  <Checkbox
                     label={t.cancellationAfterLateNotificationEnabled}
                     controller={register('cancellationAfterLateNotificationEnabled')}
                     testId={'cancellationAfterLateNotificationEnabled'}
                     disabled={!lateNotificationEnabled}
                  />
               </StyledMargin>
            </StyledFlex>

            <StyledFlex direction={'column'} gap={'10px'}>
               <Checkbox
                  label={t.earlyNotificationEnabled}
                  controller={register('earlyNotificationEnabled')}
                  testId={'earlyNotificationEnabled'}
               />
               <StyledMargin left={26}>
                  <Checkbox
                     label={t.cancellationAfterEarlyNotificationEnabled}
                     controller={register('cancellationAfterEarlyNotificationEnabled')}
                     testId={'cancellationAfterEarlyNotificationEnabled'}
                     disabled={!earlyNotificationEnabled}
                  />
               </StyledMargin>
            </StyledFlex>
         </StyledFlex>

         <FormController
            controller={register('videoConsultationReminderConfigurationValidation', {
               validate: (__, getValue) => {
                  const lateNotificationEnabled = getValue('lateNotificationEnabled');
                  const earlyNotificationEnabled = getValue('earlyNotificationEnabled');

                  if (!lateNotificationEnabled && !earlyNotificationEnabled) {
                     setError('videoConsultationReminderConfigurationValidation', true);
                     setError('lateNotificationEnabled', true);
                     setError('earlyNotificationEnabled', true);
                     return t.validations.atLeastOneFieldIsRequired;
                  }

                  return undefined;
               },
            })}
         />

         {props.children({
            handleSubmit: handleSubmit,
         })}
      </StyledFlex>
   );
};
